import { getFileRecord } from "@/api/fileRecordRefresh";
import { useRenameFileRecord } from "@/api/fileRecords";
import { cn } from "@/utils";
import { wait } from "@/utils/wait";
import type { FileRecord } from "@cohesive/api-client";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@cohesive/ui-components/ui/drawer";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

export interface SavedFileNameSheetProps {
  /** The just-saved record; the sheet is open while this is non-null. */
  record: FileRecord | null;
  /**
   * Fired exactly once per record when the sheet is finished — after a
   * successful rename, or on any dismissal (swipe-down, Done with no
   * change). The file is already saved either way; the caller navigates.
   */
  onDone: () => void;
}

function getFileName(path: string): string {
  const segments = path.split("/").filter(Boolean);
  return segments[segments.length - 1] || path;
}

function getParentPath(path: string): string {
  const lastSlash = path.lastIndexOf("/");
  return lastSlash === -1 ? "" : path.slice(0, lastSlash + 1);
}

/**
 * Split a file name into the editable stem and the locked extension.
 * Mirrors macOS Finder / Drive: only the *last* `.ext` is locked, dotfiles
 * have no extension, and files with no dot are fully editable.
 */
function splitFileName(name: string): { stem: string; ext: string } {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) {
    return { stem: name, ext: "" };
  }
  return { stem: name.slice(0, lastDot), ext: name.slice(lastDot) };
}

const PROCESSED_POLL_INTERVAL_MS = 750;
const PROCESSED_POLL_ATTEMPTS = 20; // ~15s — transcripts are tiny, ingest is usually 1-2s

/**
 * The backend's move primitive rejects records ingest hasn't marked
 * `processed` yet (400) — see EditableFileRecordName, which hides its
 * rename affordance for the same reason. The record here arrives at
 * S3-POST time, typically seconds *before* that transition, so poll
 * briefly rather than dead-ending the first Done after a fast save.
 *
 * Two consecutive fetch failures abort the poll — offline shouldn't pin
 * the user to a spinner for the whole budget. `isCancelled` lets a
 * dismissal stop the loop mid-wait.
 */
async function waitForProcessed(
  record: FileRecord,
  isCancelled: () => boolean,
): Promise<FileRecord> {
  if (record.status === "processed") return record;
  let consecutiveFailures = 0;
  for (let attempt = 0; attempt < PROCESSED_POLL_ATTEMPTS; attempt++) {
    await wait(PROCESSED_POLL_INTERVAL_MS);
    if (isCancelled()) throw new Error("Cancelled");
    let fresh: FileRecord | null = null;
    try {
      fresh = await getFileRecord(record.id);
      consecutiveFailures = 0;
    } catch {
      consecutiveFailures += 1;
      if (consecutiveFailures >= 2) {
        throw new Error("Could not check file status");
      }
    }
    if (fresh?.status === "processed") return fresh;
  }
  throw new Error("File record was not processed in time");
}

/**
 * Post-save naming step for the mobile voice flow. The file is already
 * durably saved with a default name before this opens — Voice Memos
 * style — so every exit path is safe: swipe-down or an unchanged Done
 * keeps the default, and a rename failure leaves the saved file intact.
 * The extension stays locked; only the stem is editable.
 */
export function SavedFileNameSheet({
  record,
  onDone,
}: SavedFileNameSheetProps) {
  const renameFileRecord = useRenameFileRecord();
  const [stem, setStem] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Select-all on the first focus only, so a deliberate re-focus while
  // editing doesn't blow away the caret position.
  const autoSelectedRef = useRef(false);
  // Bumped whenever the sheet finishes (dismissal included) so an
  // in-flight rename attempt goes silent instead of double-firing
  // `onDone` or toasting after the user has already left.
  const doneGenRef = useRef(0);

  const finish = () => {
    doneGenRef.current += 1;
    setIsSubmitting(false);
    onDone();
  };

  const name = record ? getFileName(record.path) : "";
  const { ext } = splitFileName(name);

  useEffect(() => {
    if (!record) return;
    const split = splitFileName(getFileName(record.path));
    setStem(split.stem);
    autoSelectedRef.current = false;
    // Focus after the drawer's open animation has mounted the input. On
    // iOS the keyboard only appears from a user gesture, so this quietly
    // does nothing there — the select-all then happens on first tap.
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [record?.id]);

  const trimmedStem = stem.trim();
  const hasSlash = trimmedStem.includes("/");
  const isValid = trimmedStem.length > 0 && !hasSlash;

  const selectStemOnFirstFocus = () => {
    if (autoSelectedRef.current) return;
    autoSelectedRef.current = true;
    inputRef.current?.setSelectionRange(0, stem.length);
  };

  const handleDone = async () => {
    if (!record || isSubmitting || !isValid) return;
    const newName = `${trimmedStem}${ext}`;
    const newPath = `${getParentPath(record.path)}${newName}`;
    if (newPath === record.path) {
      finish();
      return;
    }
    const gen = doneGenRef.current;
    const cancelled = () => doneGenRef.current !== gen;
    setIsSubmitting(true);
    try {
      const processed = await waitForProcessed(record, cancelled);
      if (cancelled()) return;
      await renameFileRecord.mutateAsync({ record: processed, newPath });
      if (cancelled()) return;
      toast.success(`Saved "${newName}"`);
      finish();
    } catch {
      if (cancelled()) return;
      // The file is still saved under its original name — stay open so
      // the user can retry or dismiss.
      toast.error(`Couldn't rename — it's saved as "${name}".`);
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      open={record !== null}
      onOpenChange={(open) => {
        // Dismissal keeps the default name and always works — the file is
        // already saved, so even a mid-rename swipe-down just abandons the
        // attempt (the generation bump silences its continuation).
        if (!open) finish();
      }}
    >
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-lc-done" />
            File saved
          </DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-3 px-4 pb-safe">
          <p className="text-sm text-muted-foreground">
            Name it so it&apos;s easy to find — or swipe down to keep the
            current name.
          </p>
          <div
            className={cn(
              "flex h-12 w-full rounded-xl border bg-background text-base",
              "focus-within:ring-2 focus-within:ring-rust",
            )}
          >
            <input
              ref={inputRef}
              value={stem}
              onChange={(e) => setStem(e.target.value)}
              onFocus={selectStemOnFirstFocus}
              placeholder="File name"
              enterKeyHint="done"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleDone();
              }}
              className="min-w-0 flex-1 bg-transparent py-2 pl-4 outline-none placeholder:text-muted-foreground"
            />
            {ext && (
              <span
                aria-hidden
                className="flex select-none items-center pr-4 text-muted-foreground"
              >
                {ext}
              </span>
            )}
          </div>
          {hasSlash && (
            <p className="px-1 text-xs text-lc-blocked">
              Names can&apos;t contain &quot;/&quot;.
            </p>
          )}
          <button
            type="button"
            onClick={() => void handleDone()}
            disabled={isSubmitting || !isValid}
            className="mb-2 flex items-center justify-center gap-2 rounded-xl bg-rust px-4 py-3.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Done
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
